import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMutationUploadRecents } from './API/useQueryRecentGames';
import {
  isLoggedInSelector,
  userNumberSelector,
} from '../redux/reducers/app/params';
import { recentLaunchedGamesSelector } from '../redux/reducers/app/storage';
import { localStorageAccessor } from '../utils/localStorage';
import { EVA_API_SERVICE_NAME } from '../constants/api';
import { useFeature } from '@growthbook/growthbook-react';
import FEATURES from '../constants/featureToggling';

const RECENT_GAMES_MIGRATION_KEY = 'recent_games_migration';

const getRecentsGamesMigrationKey = (userNumber) => {
  return [
    'casino',
    RECENT_GAMES_MIGRATION_KEY,
    userNumber,
    EVA_API_SERVICE_NAME,
  ].join(':');
};

const gameIdValidator = (id) => typeof id === 'string';

export const useMigrateRecentGames = () => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const userNumber = useSelector(userNumberSelector);
  const recentGames = useSelector(recentLaunchedGamesSelector);

  const areRecentOnBackendEnabled = useFeature(FEATURES.RECENT_GAMES_ON_BE).on;

  const ids = useMemo(() => {
    if (!recentGames?.length) {
      return [];
    }

    return recentGames.map((game) => game.id).filter(gameIdValidator);
  }, [recentGames]);

  const setMigratedKey = useCallback(() => {
    localStorageAccessor(getRecentsGamesMigrationKey(userNumber), true);
  }, [userNumber]);

  const { mutate } = useMutationUploadRecents(ids, {
    onSuccess: setMigratedKey,
  });

  useEffect(() => {
    const areGamesMigrated = localStorageAccessor(
      getRecentsGamesMigrationKey(userNumber)
    );

    if (!isLoggedIn || areGamesMigrated || !areRecentOnBackendEnabled) {
      return;
    }

    if (!ids?.length) {
      setMigratedKey();
    } else {
      mutate();
    }
  }, [
    mutate,
    isLoggedIn,
    userNumber,
    ids,
    setMigratedKey,
    areRecentOnBackendEnabled,
  ]);
};
